import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PawPrint, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, statusOf } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, NoAccess, PageHeader } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { useLiveRefresh } from "@/lib/live";
import { writesLocked } from "@/lib/splits";
import { fmtDate, todayIn } from "@/lib/format";
import { Thumb } from "@/components/inventory/util";
import { MealDialog, MedDialog, PetDialog, VaccineDialog, VisitDialog } from "@/components/pets/Dialogs";
import { Care, Docs, Log, PET_RGB, Today, Visits, useCheck } from "@/components/pets/Parts";

function Profile({ p }) {
  const rows = [["Breed", p.breed], ["Birthday", p.birthday && fmtDate(p.birthday)], ["Weight", p.weight], ["Color", p.color], ["Microchip", p.microchip && `${p.microchip}${p.registry ? ` (${p.registry})` : ""}`],
    ["License", p.license_number && `${p.license_number}${p.license_expiry ? ` · expires ${fmtDate(p.license_expiry)}` : ""}`], ["Vet", p.vet_clinic && `${p.vet_clinic}${p.vet_phone ? ` · ${p.vet_phone}` : ""}`]];
  return (
    <div className="space-y-2">
      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">{rows.filter(([, v]) => v).map(([k, v]) => <div key={k} className="min-w-0"><dt className="text-xs text-q-muted">{k}</dt><dd className="break-words text-q-text" data-testid={`pet-field-${k.toLowerCase()}`}>{v}</dd></div>)}</dl>
      {p.quirks && <p className="rounded-xl bg-q-raised p-3 text-sm text-q-text" data-testid="pet-quirks"><b>Quirks & notes:</b> {p.quirks}</p>}
    </div>
  );
}

function Detail({ base, hub, id, species, presets, kid, locked }) {
  const [p, setP] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get(`${base}/${id}`).then(({ data }) => setP(data)).catch((e) => toast.error(errMsg(e))), [base, id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "pet", 30000);
  const check = useCheck(base, p || { id }, setP);
  if (!p) return <Loader />;
  const done = (data) => { setDlg(null); if (data?.id) setP(data); else load(); };
  const open = (k, x) => setDlg({ k, x });
  const del = (kind, xid) => api.delete(`${base}/${p.id}/${kind}/${xid}`).then(({ data }) => { toast.success(kind === "visits" ? "Deleted. Its Spending entry is gone too." : "Deleted"); setP(data); }).catch((e) => toast.error(errMsg(e)));
  const addPreset = (key) => api.post(`${base}/${p.id}/meds`, { preset: key }).then(({ data }) => { toast.success("Added as a monthly medication"); setP(data); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="min-w-0 space-y-8" data-testid="pet-detail">
      <div className="flex flex-wrap items-center gap-4">
        {p.photo_id ? <Thumb url={`${base}/${p.id}/photo`} alt={p.name} className="h-24 w-24" testid="pet-photo" /> : <span className="flex h-24 w-24 items-center justify-center rounded-2xl bg-q-raised"><PawPrint className="h-8 w-8 text-q-muted" /></span>}
        <div className="min-w-0 flex-1"><h2 className="font-display text-2xl font-extrabold text-q-text" data-testid="pet-name">{p.name}</h2><p className="text-sm text-q-muted">{p.species}</p></div>
        {!kid && <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={() => open("pet")} data-testid="pet-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>}
      </div>
      <Today pet={p} check={check} adult={!kid && !locked} />
      <Profile p={p} />
      <Care pet={p} presets={presets} locked={locked} adult={!kid} open={open} del={del} addPreset={addPreset} />
      {!kid && <Visits pet={p} locked={locked} open={open} del={del} />}
      {!kid && <Docs pet={p} />}
      <Log pet={p} />
      {dlg?.k === "pet" && <PetDialog base={base} species={species} pet={p} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "meal" && <MealDialog base={base} pet={p} meal={dlg.x} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "med" && <MedDialog base={base} pet={p} med={dlg.x} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "vac" && <VaccineDialog base={base} pet={p} vac={dlg.x} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "visit" && <VisitDialog base={base} pet={p} visit={dlg.x} today={todayIn(hub.timezone)} onClose={() => setDlg(null)} onSaved={done} />}
    </div>
  );
}

export default function Pets() {
  const { hub } = useHub();
  const base = `/organizations/${hub.id}/pets`;
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [add, setAdd] = useState(false);
  const locked = writesLocked(hub);
  const load = useCallback(() => api.get(base).then(({ data }) => setD(data)).catch((e) => (statusOf(e) === 403 ? setDenied(true) : toast.error(errMsg(e)))), [base]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "pet", 30000);
  if (denied) return <NoAccess />;
  if (!d) return <Loader />;
  const sel = sp.get("p") || d.pets[0]?.id;
  const remind = (v) => api.put(`${base}/settings/reminders`, { meal_reminders: v }).then(() => { toast.success(v ? "Meal reminders on" : "Meal reminders off"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="min-w-0" data-testid="pets-page">
      <PageHeader eyebrow="Home" title="Pets" rgb={PET_RGB} testid="pets-title" />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {d.pets.map((p) => <button key={p.id} className={`q-btn min-h-[44px] ${sel === p.id ? "q-btn-primary" : "q-btn-ghost"}`} onClick={() => setSp({ p: p.id })} data-testid={`pet-tab-${p.name}`}>{p.name}</button>)}
        {!d.is_kid && <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={() => setAdd(true)} data-testid="pet-add-btn"><Plus className="h-4 w-4" /> Add pet</button>}
        {d.is_parent && <label className="ml-auto flex min-h-[44px] items-center gap-2 text-sm text-q-muted">Late-meal reminders <Switch checked={d.meal_reminders} disabled={locked} onCheckedChange={remind} data-testid="pet-meal-reminders" /></label>}
      </div>
      {!d.pets.length ? <EmptyState title="No pets yet" hint="Add your pet to track feeding, meds, vaccines and vet visits." rgb={PET_RGB} testid="pets-empty" /> :
        <Detail key={sel} base={base} hub={hub} id={sel} species={d.species} presets={d.presets} kid={d.is_kid} locked={locked} />}
      {add && <PetDialog base={base} species={d.species} onClose={() => setAdd(false)} onSaved={(p) => { setAdd(false); load(); setSp({ p: p.id }); }} />}
    </div>
  );
}
